import { useEffect, useMemo, useState } from "react";
import fallbackImg from "@apodiscounter-app/app/assets/noimage.gif";
import { buildProductPackageSizesList } from "../components/ProductCard";
import { trackAdSpiritClickEvent } from "@shared/algolia/tracking/tracking";
import { ApoAlgoliaTracking } from "../../../../../responsive_base/typescript/algolia";
import { HitProps, PackageSizeItem } from
    "@shared/algolia/results/components/ResultComponents/hit-types";
import process from 'process';


// Globals exposed by the legacy frontend
declare const ShoppingCart: {
  addCartItem: (
    id: number,
    amount: number,
    prescriptionType: number,
    pzn: string,
    algoliaQueryId: string
  ) => void;
};

declare const SubscriptionShoppingCart: {
  addCartItem: (id: number) => void;
};


/**
 * useProductCard
 *
 * Central hook for all product-card state and handlers.
 * Shared between block (grid) and list layouts.
 */

export const useProductCard = (hit: HitProps["hit"]) => {
  // Raw shipping limit
  const dataShippingLimit = Number(
    document
      .querySelector('[data-selector="searchresults"]')
      ?.getAttribute("data-shipping-limit") ||
    document
      .querySelector('[data-selector="category"]')
      ?.getAttribute("data-shipping-limit")
  );

  //  Base state for current package size
  const [hitState, setHitState] = useState(() => ({
    ...hit,
    status_number: Number(hit.status_number),
    amount: 1,
    currentPackageSizePZN: hit.pzn,
  }));

  // Image fallback / preload / onError handling
  const [imageWithFallback, setImageWithFallback] = useState(hit.image_url);

  const onImageError = () => setImageWithFallback(fallbackImg);

  useEffect(() => {
    const img = new Image();
    img.src = imageWithFallback;
    const timeout = 1000;
    const timer = setTimeout(onImageError, timeout);

    img.onload = () => clearTimeout(timer);
    img.onerror = () => {
      clearTimeout(timer);
      onImageError();
    };
  }, [imageWithFallback]);

  // Package sizes list
  const packageSizesList = buildProductPackageSizesList(
    hit,
    hit.alternative_package_sizes
  );

  const handleChangePackageSize = (item: PackageSizeItem) => {
    setHitState((prev) => ({
      ...prev,
      ...item,
      status_number: Number(item.status_number),
      currentPackageSizePZN: item.pzn,
    }));
    setImageWithFallback(item.image_url);
  };

  // Amount handlers
  const increaseAmount = () =>
    hitState.amount < 20 &&
    setHitState((prev) => ({ ...prev, amount: prev.amount + 1 }));

  const decreaseAmount = () =>
    hitState.amount > 1 &&
    setHitState((prev) => ({ ...prev, amount: prev.amount - 1 }));

  // Prescription, availability and subscription flags
  const isRxProduct = hitState.prescription_required === 1;
  const isAvailable = hitState.status_number < 10;
  const subscriptionAvailable = hit.subscription_status === 1;

  // Product Link
  const baseUrl = process.env.URI || window.location.origin;
  const productLink = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    const query = qs.get("query") || "";

    const params = new URLSearchParams();
    if (hitState.objectID)   params.append("objectID", hitState.objectID);
    if (hitState.__queryID)  params.append("queryID", hitState.__queryID);
    if (query)               params.append("query", query);

    const parsed = new URL(hitState.product_url, baseUrl);
    const pathAndSearch = parsed.pathname + parsed.search;

    return `${baseUrl}${pathAndSearch}?${params.toString()}`;
  }, [
    hitState.product_url,
    hitState.objectID,
    hitState.__queryID
  ]);

  // Add to cart
  const addToCart = () => {
    ShoppingCart.addCartItem(
      Number(hitState.products_id),
      hitState.amount,
      2,
      hitState.pzn.toString(),
      hitState.__queryID?.toString() || ""
    );
    // Use ApoAlgoliaTracking for authenticated token tracking
    const tracker = ApoAlgoliaTracking.getInstance();
    tracker.trackCartButtonClick(
      hitState.__queryID?.toString() || "",
      hitState.pzn.toString()
    );

    if (hit.isPromoted && hit.adSpiritClickUrl) {
      trackAdSpiritClickEvent(hit.adSpiritClickUrl);
    }
  };

  // Subscription handler
  const addSubscription = () =>
    SubscriptionShoppingCart.addCartItem(Number(hitState.products_id));

  return {
    hitState,
    imageWithFallback,
    packageSizesList,
    dataShippingLimit,
    isRxProduct,
    isAvailable,
    subscriptionAvailable,
    productLink,

    onImageError,
    handleChangePackageSize,
    increaseAmount,
    decreaseAmount,
    addToCart,
    addSubscription,
  };
};